import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, OnDestroy, OnInit, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { FormControl, FormsModule, Validators } from '@angular/forms';
import { MatOption } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSelect } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subscription, combineLatest } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { BullhornFieldType, Entities } from '../../data-rules/data-rules-model';
import { ICustomPositioningConfig } from '../../data-rules/lib/custom-component-overlay.directive';
import { IEntityItem, PairedAutocompleteComponent } from '../../data-rules/lib/paired-autocomplete/paired-autocomplete.component';
import { StaticOptionsProvider } from '../../data-rules/lib/option-items-providers';
import { IActionFieldMeta, IFieldMeta, MetaDataService, MetaSource } from '../../data-rules/meta-data.service';
import { LoadingComponent } from '../../loading/loading.component';
import { MessageNotificationService } from '../../shared/services/message-notification.service';
import { EnrichmentFieldMappingsService } from './enrichment-field-mappings.service';

interface IEnrichedField {
  key: string;
  label: string;
  icon: string;
  brandIcon?: string;
}

interface IFieldGroup {
  key: string;
  label: string;
  fields: IEnrichedField[];
  collapsedByDefault?: boolean;
}

const REQUIRED_FIELD_KEYS = new Set<string>([
  'candidateiq.trust_score',
  'candidateiq.point_of_contact_id',
]);

const FULL_RESPONSE_FIELD_KEY = 'pdl.data';
const FULL_RESPONSE_ALLOWED_FIELD_NAMES = new Set<string>(
  Array.from({ length: 10 }, (_, i) => `customTextBlock${i + 1}`)
);

const FIELD_GROUPS: IFieldGroup[] = [
  {
    key: 'candidateiq',
    label: 'Enrichment',
    fields: [
      { key: 'candidateiq.trust_score',         icon: 'verified_user', label: 'Validation Score' },
      { key: 'candidateiq.point_of_contact_id', icon: 'contact_page',  label: 'Enrichment Data Record ID' },
    ]
  },
  {
    key: 'fullResponse',
    label: 'Full Response',
    fields: [
      { key: FULL_RESPONSE_FIELD_KEY, icon: 'data_object', label: 'Full Response'}
    ]
  },
  {
    key: 'personal',
    label: 'Personal Information',
    fields: [
      { key: 'pdl.data.first_name',                icon: 'person',       label: 'First Name' },
      { key: 'pdl.data.last_name',                 icon: 'person',       label: 'Last Name' },
      { key: 'pdl.data.middle_name',               icon: 'person',       label: 'Middle Name' },
      { key: 'pdl.data.middle_initial',            icon: 'person',       label: 'Middle Initial' },
      { key: 'pdl.data.full_name',                 icon: 'badge',        label: 'Full Name' },
      { key: 'pdl.data.gender',                    icon: 'wc',           label: 'Gender' },
      { key: 'pdl.data.birth_year',                icon: 'cake',         label: 'Birth Year' },
      { key: 'pdl.data.birth_date',                icon: 'cake',         label: 'Birth Date' },
      { key: 'pdl.data.summary',                   icon: 'description',  label: 'Profile Summary' },
      { key: 'pdl.data.inferred_salary',           icon: 'payments',     label: 'Inferred Salary' },
      { key: 'pdl.data.inferred_years_experience', icon: 'work_history', label: 'Years of Experience' },
      { key: 'pdl.data.linkedin_connections',      icon: 'public', brandIcon: 'linkedin', label: 'LinkedIn Connections' },
    ]
  },
  {
    key: 'contact',
    label: 'Contact',
    fields: [
      { key: 'pdl.data.work_email',                 icon: 'email',      label: 'Work Email' },
      { key: 'pdl.data.recommended_personal_email', icon: 'mail',       label: 'Personal Email (Recommended)' },
      { key: 'pdl.data.mobile_phone',               icon: 'smartphone', label: 'Mobile Phone' },
    ]
  },
  {
    key: 'social',
    label: 'Social Profiles',
    collapsedByDefault: true,
    fields: [
      { key: 'pdl.data.linkedin_url',      icon: 'public', brandIcon: 'linkedin', label: 'LinkedIn URL' },
      { key: 'pdl.data.linkedin_username', icon: 'public', brandIcon: 'linkedin', label: 'LinkedIn Username' },
      { key: 'pdl.data.linkedin_id',       icon: 'public', brandIcon: 'linkedin', label: 'LinkedIn ID' },
      { key: 'pdl.data.facebook_url',      icon: 'public', brandIcon: 'facebook', label: 'Facebook URL' },
      { key: 'pdl.data.facebook_username', icon: 'public', brandIcon: 'facebook', label: 'Facebook Username' },
      { key: 'pdl.data.facebook_id',       icon: 'public', brandIcon: 'facebook', label: 'Facebook ID' },
      { key: 'pdl.data.twitter_url',       icon: 'public', brandIcon: 'twitter',  label: 'Twitter / X URL' },
      { key: 'pdl.data.twitter_username',  icon: 'public', brandIcon: 'twitter',  label: 'Twitter / X Username' },
      { key: 'pdl.data.github_url',        icon: 'public', brandIcon: 'github',   label: 'GitHub URL' },
      { key: 'pdl.data.github_username',   icon: 'public', brandIcon: 'github',   label: 'GitHub Username' },
    ]
  },
  {
    key: 'job',
    label: 'Current Job',
    fields: [
      { key: 'pdl.data.job_title',         icon: 'work',        label: 'Job Title' },
      { key: 'pdl.data.job_title_class',   icon: 'category',    label: 'Job Title Class' },
      { key: 'pdl.data.industry',          icon: 'factory',     label: 'Industry' },
      { key: 'pdl.data.job_start_date',    icon: 'event',       label: 'Job Start Date' },
      { key: 'pdl.data.job_last_updated',  icon: 'update',      label: 'Job Last Updated' },
      { key: 'pdl.data.job_last_changed',  icon: 'update',      label: 'Job Last Changed' },
      { key: 'pdl.data.job_last_verified', icon: 'verified',    label: 'Job Last Verified' },
      { key: 'pdl.data.job_summary',       icon: 'description', label: 'Job Summary' },
    ]
  },
  {
    key: 'company',
    label: 'Current Company',
    collapsedByDefault: true,
    fields: [
      { key: 'pdl.data.job_company_name',         icon: 'business',   label: 'Company Name' },
      { key: 'pdl.data.job_company_website',      icon: 'language',   label: 'Company Website' },
      { key: 'pdl.data.job_company_size',         icon: 'groups',     label: 'Company Size' },
      { key: 'pdl.data.job_company_founded',      icon: 'foundation', label: 'Company Founded' },
      { key: 'pdl.data.job_company_industry',     icon: 'factory',    label: 'Company Industry' },
      { key: 'pdl.data.job_company_type',         icon: 'category',   label: 'Company Type' },
      { key: 'pdl.data.job_company_ticker',       icon: 'show_chart', label: 'Company Ticker' },
      { key: 'pdl.data.job_company_linkedin_url', icon: 'public', brandIcon: 'linkedin', label: 'Company LinkedIn URL' },
      { key: 'pdl.data.job_company_facebook_url', icon: 'public', brandIcon: 'facebook', label: 'Company Facebook URL' },
      { key: 'pdl.data.job_company_twitter_url',  icon: 'public', brandIcon: 'twitter',  label: 'Company Twitter URL' },
    ]
  },
  {
    key: 'company_location',
    label: 'Current Company Location',
    collapsedByDefault: true,
    fields: [
      { key: 'pdl.data.job_company_location_name',           icon: 'location_on',        label: 'Location Name' },
      { key: 'pdl.data.job_company_location_locality',       icon: 'location_city',      label: 'City' },
      { key: 'pdl.data.job_company_location_region',         icon: 'map',                label: 'State / Region' },
      { key: 'pdl.data.job_company_location_country',        icon: 'public',             label: 'Country' },
      { key: 'pdl.data.job_company_location_postal_code',    icon: 'local_post_office',  label: 'Postal Code' },
      { key: 'pdl.data.job_company_location_street_address', icon: 'home',               label: 'Street Address' },
      { key: 'pdl.data.job_company_location_address_line_2', icon: 'home',               label: 'Address Line 2' },
      { key: 'pdl.data.job_company_location_continent',      icon: 'travel_explore',     label: 'Continent' },
      { key: 'pdl.data.job_company_location_metro',          icon: 'apartment',          label: 'Metro Area' },
      { key: 'pdl.data.job_company_location_geo',            icon: 'my_location',        label: 'Geo Coordinates' },
    ]
  },
  {
    key: 'location',
    label: 'Personal Location',
    fields: [
      { key: 'pdl.data.location_name',           icon: 'location_on',       label: 'Location Name' },
      { key: 'pdl.data.location_locality',       icon: 'location_city',     label: 'City' },
      { key: 'pdl.data.location_region',         icon: 'map',               label: 'State / Region' },
      { key: 'pdl.data.location_country',        icon: 'public',            label: 'Country' },
      { key: 'pdl.data.location_postal_code',    icon: 'local_post_office', label: 'Postal Code' },
      { key: 'pdl.data.location_street_address', icon: 'home',              label: 'Street Address' },
      { key: 'pdl.data.location_address_line_2', icon: 'home',              label: 'Address Line 2' },
      { key: 'pdl.data.location_continent',      icon: 'travel_explore',    label: 'Continent' },
      { key: 'pdl.data.location_metro',          icon: 'apartment',         label: 'Metro Area' },
      { key: 'pdl.data.location_geo',            icon: 'my_location',       label: 'Geo Coordinates' },
      { key: 'pdl.data.location_last_updated',   icon: 'update',            label: 'Location Last Updated' },
    ]
  },
  {
    key: 'experience',
    label: 'Experience',
    collapsedByDefault: true,
    fields: [
      { key: 'pdl.data.experience.title',      icon: 'work',        label: 'Job Title' },
      { key: 'pdl.data.experience.company',    icon: 'business',    label: 'Company Name' },
      { key: 'pdl.data.experience.start_date', icon: 'event',       label: 'Start Date' },
      { key: 'pdl.data.experience.end_date',   icon: 'event',       label: 'End Date' },
      { key: 'pdl.data.experience.summary',    icon: 'description', label: 'Summary' },
    ]
  },
  {
    key: 'education',
    label: 'Education',
    collapsedByDefault: true,
    fields: [
      { key: 'pdl.data.education.school',     icon: 'school',      label: 'School Name' },
      { key: 'pdl.data.education.degrees',    icon: 'school',      label: 'Degrees' },
      { key: 'pdl.data.education.majors',     icon: 'menu_book',   label: 'Majors' },
      { key: 'pdl.data.education.start_date', icon: 'event',       label: 'Start Date' },
      { key: 'pdl.data.education.end_date',   icon: 'event',       label: 'End Date' },
      { key: 'pdl.data.education.gpa',        icon: 'grade',       label: 'GPA' },
    ]
  },
  {
    key: 'skills',
    label: 'Skills & Interests',
    collapsedByDefault: true,
    fields: [
      { key: 'pdl.data.skills',                      icon: 'psychology',  label: 'Skills' },
      { key: 'pdl.data.interests',                   icon: 'favorite',    label: 'Interests' },
      { key: 'pdl.data.certifications.name',         icon: 'verified',    label: 'Certification Name' },
      { key: 'pdl.data.certifications.organization', icon: 'apartment',   label: 'Certification Organisation' },
      { key: 'pdl.data.languages.name',              icon: 'translate',   label: 'Language' },
    ]
  },
];

const STRATEGY_OPTIONS = [
  { value: 'empty_only', label: 'Overwrite if empty' },
  { value: 'overwrite',  label: 'Always overwrite' },
  { value: 'ignore',     label: 'Ignore' },
];


const DEFAULT_MAPPINGS: Record<string, string> = {
  // Personal
  'pdl.data.first_name':                 'firstName',
  'pdl.data.last_name':                  'lastName',
  'pdl.data.middle_name':                'middleName',
  // Contact
  'pdl.data.work_email':                 'email',
  'pdl.data.recommended_personal_email': 'email2',
  'pdl.data.mobile_phone':               'mobile',
  // Current Job
  'pdl.data.job_title':                  'occupation',
  'pdl.data.job_company_name':           'companyName',
  'pdl.data.job_company_website':        'companyURL',
  // Professional
  'pdl.data.summary':                    'description',
};

@Component({
  selector: 'app-enrichment-field-mappings',
  templateUrl: './enrichment-field-mappings.component.html',
  styleUrls: ['./enrichment-field-mappings.component.scss', '../../../assets/tables.scss'],
  imports: [FormsModule, MatSelect, MatOption, MatTooltipModule, PairedAutocompleteComponent, LoadingComponent]
})
export class EnrichmentFieldMappingsComponent implements OnInit, AfterViewInit, OnDestroy {

  @Output()
  needsSavingEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChild('allFieldsCheckbox')
  private allFieldsCheckboxRef: ElementRef<HTMLInputElement>;

  @ViewChild('searchInput')
  private searchInputRef: ElementRef<HTMLInputElement>;

  // Captured in DOM order matching allEnrichedFields (rows are hidden via CSS, never removed from DOM)
  @ViewChildren(PairedAutocompleteComponent)
  pickers: QueryList<PairedAutocompleteComponent>;

  readonly fieldGroups: IFieldGroup[] = FIELD_GROUPS;
  readonly allEnrichedFields: IEnrichedField[] = FIELD_GROUPS.flatMap(g => g.fields);
  readonly strategyOptions = STRATEGY_OPTIONS;

  loading = true;
  collapsedGroups = new Set<string>(FIELD_GROUPS.filter(g => g.collapsedByDefault).map(g => g.key));
  searchTerm = '';
  hasNoResults = false;
  fieldEnabled: Record<string, boolean> = {};
  allFieldsEnabled = true;
  rowHidden: Record<string, boolean> = {};
  groupHeaderHidden: Record<string, boolean> = {};
  private visibleFieldKeys: Set<string> | null = null;
  private searchTimer: ReturnType<typeof setTimeout>;

  // Read directly by EnrichmentSettingsComponent for saving
  mappings: Record<string, string> = {};
  strategies: Record<string, string> = {};

  // selectedFieldControl for the paired autocomplete — stores just the sub-field name (e.g. "city")
  fieldControls = new Map<string, FormControl>();
  // secondaryEntityControl — stores the entity descriptor (e.g. "address", or null for primary fields)
  entityControls = new Map<string, FormControl>();

  // Shared inputs for all paired autocompletes
  entitiesOptions: IEntityItem[] = [];
  readonly primaryEntity: Entities = 'Candidate' as Entities;
  readonly dataSourceType = MetaSource.ACTION;
  candidateEntityLabel = 'Candidate';
  readonly overlayPositioningConfig: ICustomPositioningConfig = {
    originX: 'center',
    originY: 'bottom',
    overlayX: 'center',
    overlayY: 'top',
    offsetY: 8,
    offsetX: -20
  };

  private addressFieldsMap = new Map<string, IActionFieldMeta>();
  private pickersInitialized = false;
  private subscriptions: Subscription[] = [];

  constructor(
    private service: EnrichmentFieldMappingsService,
    private metaDataService: MetaDataService,
    private messageNotificationService: MessageNotificationService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.allEnrichedFields.forEach(f => this.fieldEnabled[f.key] = true);
    // Ensure meta is loaded, then fetch entities options and saved config in parallel
    this.service.getAvailableFields().pipe(
      mergeMap(() => combineLatest([
        this.metaDataService.reduceMetaToEntitiesOptions(this.primaryEntity, MetaSource.ACTION),
        this.metaDataService.getActionMeta(this.primaryEntity)
      ])),
      mergeMap(([entities, actionFields]) => {
        const addressEntities: IEntityItem[] = actionFields
          .filter(f => (f.metaType as string) === 'ADDRESS' && f.addressFields?.length)
          .map(f => {
            this.addressFieldsMap.set(f.fieldName, f);
            return { value: f.fieldName, label: f.label || f.fieldName, secondaryDescriptor: f.fieldName, description: this.primaryEntity as string };
          });
        this.entitiesOptions = [...entities, ...addressEntities];
        return this.service.getFieldMappings();
      })
    ).subscribe({
      next: config => {
        this.candidateEntityLabel = this.metaDataService.getEntitiesLabelsMap(MetaSource.ACTION)[this.primaryEntity] || this.primaryEntity;

        const savedMappings = config.mappings ?? {};
        const hasExistingMappings = Object.keys(savedMappings).length > 0;
        this.mappings = hasExistingMappings ? savedMappings : { ...DEFAULT_MAPPINGS };
        this.strategies = { ...(config.strategies ?? {}) };

        let appliedStrategyDefaults = false;
        this.allEnrichedFields.forEach(field => {
          if (this.mappings[field.key] && !this.strategies[field.key]) {
            this.strategies[field.key] = 'empty_only';
            appliedStrategyDefaults = true;
          }
        });

        this.allEnrichedFields.forEach(field => {
          this.fieldEnabled[field.key] = true;

          const fullPath = this.mappings[field.key] ?? null;
          const { entityDescriptor, fieldName } = this.parseFullPath(fullPath);

          // fieldControl: just the sub-field name part (e.g. "city", "customText1")
          const fieldCtrl = new FormControl(fieldName, REQUIRED_FIELD_KEYS.has(field.key) ? Validators.required : null);
          // entityControl: secondary entity descriptor (e.g. "address", or null for top-level fields)
          const entityCtrl = new FormControl(entityDescriptor);

          // When field selection changes, reconstruct the full dot-notation path for saving
          const sub = fieldCtrl.valueChanges.subscribe(value => {
            const entity = entityCtrl.value;
            const fullNewPath = value ? (entity ? `${entity}.${value}` : value) : null;
            if (fullNewPath) {
              this.mappings[field.key] = fullNewPath;
            } else {
              delete this.mappings[field.key];
            }
            this.needsSavingEmitter.emit(true);
          });
          this.subscriptions.push(sub);

          this.fieldControls.set(field.key, fieldCtrl);
          this.entityControls.set(field.key, entityCtrl);
        });

        this.loading = false;
        this.rebuildVisibility();
        if (!hasExistingMappings || appliedStrategyDefaults) {
          this.needsSavingEmitter.emit(true);
        }
        // QueryList.changes fires once the @if (!loading) block renders the pickers
      },
      error: err => {
        console.error(err);
        this.loading = false;
        this.messageNotificationService.error(
          'There was an error loading enrichment settings. Please try reloading the page.',
          err
        );
      }
    });
  }

  ngAfterViewInit(): void {
    this.pickers.changes.subscribe((list: QueryList<PairedAutocompleteComponent>) => {
      if (list.length > 0 && !this.pickersInitialized) {
        this.pickersInitialized = true;
        this.initPickers();
      }
    });

    // If loading resolved synchronously (USE_MOCK_DATA path), pickers are already in the
    // DOM by the time this hook runs and QueryList.changes will never fire — initialise directly.
    if (this.pickers.length > 0 && !this.pickersInitialized) {
      this.pickersInitialized = true;
      this.initPickers();
    }

    this.updateHeaderCheckbox();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
    clearTimeout(this.searchTimer);
  }

  toggleGroup(key: string): void {
    if (this.collapsedGroups.has(key)) {
      this.collapsedGroups.delete(key);
    } else {
      this.collapsedGroups.add(key);
    }
    this.rebuildVisibility();
  }

  isGroupCollapsed(key: string): boolean {
    return this.collapsedGroups.has(key);
  }

  onStrategyChange(key: string, value: string): void {
    this.strategies[key] = value;
    this.needsSavingEmitter.emit(true);
  }

  @HostListener('document:keydown', ['$event'])
  onGlobalKeydown(event: KeyboardEvent): void {
    if (this.dialog.openDialogs.length === 0 && event.ctrlKey && event.shiftKey && event.key.toLowerCase() === 'f') {
      event.preventDefault();
      this.searchInputRef?.nativeElement.focus();
    }
  }

  onSearchInput(event: Event): void {
    const term = (event.target as HTMLInputElement).value;
    this.searchTerm = term;
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.applySearch(term), 150);
  }

  clearSearch(): void {
    clearTimeout(this.searchTimer);
    this.searchTerm = '';
    this.visibleFieldKeys = null;
    this.hasNoResults = false;
    this.rebuildVisibility();
  }

  isRequiredField(key: string): boolean {
    return REQUIRED_FIELD_KEYS.has(key);
  }

  hasRequiredMappingsConfigured(): boolean {
    return [...REQUIRED_FIELD_KEYS].every(key => !!this.mappings[key]);
  }

   private applySearch(term: string): void {
    const trimmed = term.trim().toLowerCase();
    if (!trimmed) {
      this.visibleFieldKeys = null;
      this.hasNoResults = false;
    } else {
      this.visibleFieldKeys = new Set(
        FIELD_GROUPS.flatMap(g => g.fields)
          .filter(f => f.label.toLowerCase().includes(trimmed))
          .map(f => f.key)
      );
      this.hasNoResults = this.visibleFieldKeys.size === 0;
    }
    this.rebuildVisibility();
  }

  private rebuildVisibility(): void {
    const rowHidden: Record<string, boolean> = {};
    const groupHeaderHidden: Record<string, boolean> = {};

    for (const group of FIELD_GROUPS) {
      let hasVisibleField = false;
      for (const field of group.fields) {
        const hidden = this.visibleFieldKeys !== null
          ? !this.visibleFieldKeys.has(field.key)
          : this.collapsedGroups.has(group.key);
        rowHidden[field.key] = hidden;
        if (!hidden) {
          hasVisibleField = true;
        }
      }
      groupHeaderHidden[group.key] = this.visibleFieldKeys !== null && !hasVisibleField;
    }

    this.rowHidden = rowHidden;
    this.groupHeaderHidden = groupHeaderHidden;
  }

  toggleField(key: string): void {
    if (REQUIRED_FIELD_KEYS.has(key)) return;
    this.fieldEnabled[key] = !this.fieldEnabled[key];
    this.updateHeaderCheckbox();
    this.needsSavingEmitter.emit(true);
  }

  toggleAllFields(): void {
    const optionalFields = this.allEnrichedFields.filter(f => !REQUIRED_FIELD_KEYS.has(f.key));
    const newState = !optionalFields.every(f => this.fieldEnabled[f.key]);
    optionalFields.forEach(f => { this.fieldEnabled[f.key] = newState; });
    this.allFieldsEnabled = newState;
    if (this.allFieldsCheckboxRef) {
      this.allFieldsCheckboxRef.nativeElement.indeterminate = false;
    }
    this.needsSavingEmitter.emit(true);
  }

  private updateHeaderCheckbox(): void {
    const optionalFields = this.allEnrichedFields.filter(f => !REQUIRED_FIELD_KEYS.has(f.key));
    const enabledCount = optionalFields.filter(f => this.fieldEnabled[f.key]).length;
    this.allFieldsEnabled = enabledCount === optionalFields.length;
    if (this.allFieldsCheckboxRef) {
      this.allFieldsCheckboxRef.nativeElement.indeterminate = enabledCount > 0 && enabledCount < optionalFields.length;
    }
  }

  private initPickers(): void {
    this.pickers.forEach((picker, i) => {
      const field = this.allEnrichedFields[i];
      const entityDescriptor = this.entityControls.get(field.key).value;

      // Set initial options for this picker based on the current entity selection
      this.loadPickerOptions(picker, field.key, entityDescriptor);

      // When the entity picker changes, clear the field and reload options for the new entity
      const sub = this.entityControls.get(field.key).valueChanges.subscribe(entity => {
        this.fieldControls.get(field.key).setValue(null);
        this.loadPickerOptions(picker, field.key, entity);
      });
      this.subscriptions.push(sub);
    });
  }

  private loadPickerOptions(picker: PairedAutocompleteComponent, fieldKey: string, entityDescriptor: string | null): void {
    const addressField = entityDescriptor ? this.addressFieldsMap.get(entityDescriptor) : null;
    if (addressField) {
      const options = (addressField.addressFields ?? []).map(af => ({ name: af.fieldName, label: af.label || af.fieldName }));
      picker.setOptionsProvider(new StaticOptionsProvider(options), this.fieldControls.get(fieldKey).value);
      return;
    }
    this.metaDataService.filterMetaBySecondaryDescriptor(this.primaryEntity, entityDescriptor, MetaSource.ACTION)
      .subscribe((meta: IFieldMeta[]) => {
        const options = meta
          .filter(fm => fm.metaType === BullhornFieldType.STRING || fm.metaType === BullhornFieldType.INTEGER)
          .filter(fm => fieldKey !== FULL_RESPONSE_FIELD_KEY || FULL_RESPONSE_ALLOWED_FIELD_NAMES.has(fm.fieldName))
          .map(fm => ({ name: fm.fieldName, label: fm.label }));
        picker.setOptionsProvider(new StaticOptionsProvider(options), this.fieldControls.get(fieldKey).value);
      });
  }

  // Splits "address.city" → { entityDescriptor: "address", fieldName: "city" }
  // Splits "customText1" → { entityDescriptor: null, fieldName: "customText1" }
  private parseFullPath(fullPath: string | null): { entityDescriptor: string | null; fieldName: string | null } {
    if (!fullPath) return { entityDescriptor: null, fieldName: null };
    const dotIndex = fullPath.indexOf('.');
    if (dotIndex === -1) return { entityDescriptor: null, fieldName: fullPath };
    return {
      entityDescriptor: fullPath.substring(0, dotIndex),
      fieldName: fullPath.substring(dotIndex + 1)
    };
  }
}
